/**
 * ドライバー表示設定フォームコンポーネント
 * FL-229: ドライバーに陸送料金を表示するかどうかを制御する設定
 */

import React from 'react';
import { Button } from '../ui';
import logger from '../../utils/logger';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface DriverVisibilitySettingsFormProps {
  showTransportFeeToDriver: boolean;
  onChange: (show: boolean) => void;
  onSave: () => Promise<void>;
  isLoading?: boolean;
}

const DriverVisibilitySettingsForm: React.FC<DriverVisibilitySettingsFormProps> = ({
  showTransportFeeToDriver,
  onChange,
  onSave,
  isLoading = false,
}) => {
  const handleVisibilityChange = (show: boolean) => {
    logger.info('🔄 ドライバー表示設定変更', {
      from: showTransportFeeToDriver,
      to: show,
    });
    onChange(show);
  };

  const handleSave = async () => {
    logger.info('🔄 ドライバー表示設定保存開始', { showTransportFeeToDriver });
    try {
      await onSave();
    } catch (error) {
      logger.error('❌ ドライバー表示設定保存エラー', { error });
      // エラーハンドリング（UI フィードバック）は親コンポーネント（SettingsPage）で実施
      // throw error; // 親で処理するため、エラーを伝播
    }
  };

  return (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          ドライバーへの陸送料金表示設定
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-300'>
          ドライバーが案件の陸送料金を確認できるかどうかを制御します。
          この設定により、案件詳細画面での料金表示が制御されます。
        </p>
      </div>

      <div className='space-y-4'>
        {/* 表示しない設定 */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            !showTransportFeeToDriver
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => handleVisibilityChange(false)}
        >
          <div className='flex items-start space-x-3'>
            <div
              className={`mt-1 ${
                !showTransportFeeToDriver
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <EyeSlashIcon className='h-5 w-5' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center space-x-2'>
                <input
                  type='radio'
                  checked={!showTransportFeeToDriver}
                  onChange={() => handleVisibilityChange(false)}
                  className='h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <label className='text-sm font-medium text-gray-900 dark:text-white'>
                  表示しない（非表示）
                </label>
              </div>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                ドライバーは案件の陸送料金を確認できません。
                料金情報が必要ない場合、またはセキュリティ上の理由で非表示にしたい場合に選択してください。
              </p>
            </div>
          </div>
        </div>

        {/* 表示する設定 */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            showTransportFeeToDriver
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => handleVisibilityChange(true)}
        >
          <div className='flex items-start space-x-3'>
            <div
              className={`mt-1 ${
                showTransportFeeToDriver
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <EyeIcon className='h-5 w-5' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center space-x-2'>
                <input
                  type='radio'
                  checked={showTransportFeeToDriver}
                  onChange={() => handleVisibilityChange(true)}
                  className='h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <label className='text-sm font-medium text-gray-900 dark:text-white'>
                  表示する（公開）
                </label>
              </div>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                ドライバーが案件詳細画面で陸送料金を確認できます。
                透明性が重要な場合や、ドライバーの金額把握が必要な場合に選択してください。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 現在の設定表示 */}
      <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4'>
        <div className='flex items-center space-x-2'>
          {showTransportFeeToDriver ? (
            <EyeIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
          ) : (
            <EyeSlashIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
          )}
          <span className='text-sm text-gray-600 dark:text-gray-300'>
            現在の設定:
            <strong className='ml-1'>
              {showTransportFeeToDriver ? '陸送料金は表示' : '陸送料金は非表示'}
            </strong>
          </span>
        </div>
      </div>

      {/* 保存ボタン */}
      <div className='flex justify-end'>
        <Button variant='primary' onClick={handleSave} disabled={isLoading} className='px-6'>
          {isLoading ? '保存中...' : '設定を保存'}
        </Button>
      </div>
    </div>
  );
};

export default DriverVisibilitySettingsForm;
